import React, { useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  Users,
  Shield,
  BarChart3,
  FileText,
  PhoneCall,
  MapPin,
  Globe,
  AlertTriangle,
  HeartHandshake,
  Eye,
  EyeOff,
} from "lucide-react";

import Button from "../UI/Button";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false); // ✅ added only

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const toastId = "login-toast";
    toast.loading("Signing in...", { id: toastId });

    try {
      const result = await login(email, password);

      if (result.success) {
        toast.success("Login successful!", { id: toastId });
        navigate("/dashboard");
      } else {
        setError(result.error);
        toast.error(result.error || "Login failed.", { id: toastId });
      }
    } catch (err) {
      const msg = err?.message || "Something went wrong during login.";
      setError(msg);
      toast.error(msg, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-slate-100">

      {/* NAVBAR */}
      <div className="flex justify-between items-center px-6 lg:px-20 py-4 border-b border-slate-700">
        <h1 className="text-xl font-bold text-emerald-400">
          Village of Hope
        </h1>

        <div className="flex gap-3">
          <button
            onClick={() => setShowLoginModal(true)}
            className="px-5 py-2 rounded-xl border border-slate-600 hover:bg-slate-700 transition"
          >
            Login
          </button>

          <button
            onClick={() => navigate("/donate")}
            className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:opacity-90 font-semibold shadow-lg"
          >
            Donate
          </button>
        </div>
      </div>

      {/* HERO SECTION */}
      <div className="px-6 lg:px-20 py-16 grid md:grid-cols-2 gap-12 items-center">

        {/* LEFT SIDE */}
        <div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-900/40 text-emerald-300 text-sm font-semibold border border-emerald-700">
            <Shield className="w-4 h-4" />
            Safe records • Secure access • Better care
          </div>

          <h1 className="mt-6 text-4xl lg:text-5xl font-extrabold leading-tight">
            Giving Every Child{" "}
            <span className="text-emerald-400">Hope for Tomorrow</span>
          </h1>

          <p className="mt-5 text-slate-300 text-lg max-w-xl leading-relaxed">
            At Village of Hope, every child deserves a safe home, proper care, and
            the opportunity to grow. Our system ensures that each child’s journey is
            guided, supported, and never overlooked.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Feature icon={<Users />} title="Children Records" />
            <Feature icon={<BarChart3 />} title="Reports & Analytics" />
            <Feature icon={<FileText />} title="Case Documentation" />
            <Feature icon={<Shield />} title="Secure Role Access" />
          </div>
        </div>

        {/* RIGHT SIDE - PROFESSIONAL PROMOTION */}
          <div className="w-full max-w-md mx-auto">

            <div className="relative bg-slate-800/90 backdrop-blur-xl shadow-2xl p-8 rounded-3xl border border-slate-700 overflow-hidden">

              {/* subtle glow */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/20 blur-3xl rounded-full"></div>

              {/* LOGO */}
              <div className="flex justify-center mb-6">
                <img
                  src="/assets/hope.png"
                  alt="Village of Hope"
                  className="h-24 object-contain"
                />
              </div>

              {/* TITLE */}
              <h2 className="text-xl font-semibold text-center text-emerald-400">
                Supporting Children, Building Futures
              </h2>

              {/* CONTENT */}
              <p className="mt-5 text-slate-300 text-sm leading-relaxed text-center">
                Your support helps provide shelter, education, and care for children in need.
                Every contribution creates a meaningful impact in shaping their future.
              </p>

              {/* TRUST BADGES */}
              <div className="mt-6 flex justify-center gap-3 text-xs text-slate-400">
                <span className="px-3 py-1 rounded-full bg-slate-700">Secure</span>
                <span className="px-3 py-1 rounded-full bg-slate-700">Transparent</span>
                <span className="px-3 py-1 rounded-full bg-slate-700">Impactful</span>
              </div>

            </div>
          </div>
      </div>

      {/* HOTLINES SECTION */}
      <div className="py-20 px-6 lg:px-20">
        
        <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-emerald-400">
              Contact & Support Services
            </h2>
            <p className="text-slate-400 mt-2">
              Reach out anytime for assistance, emergencies, or support services.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">

          <div>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/hotline-poster.png"
                alt="Hotline Poster"
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-6">

            <HotlineCard
              icon={<PhoneCall />}
              title="Rescue & Emergencies"
              number="+63 123 456 7890"
            />

            <HotlineCard
              icon={<HeartHandshake />}
              title="Counseling & Support"
              number="+63 987 654 3210"
            />

            <HotlineCard
              icon={<AlertTriangle />}
              title="Reporting Child Abuse"
              number="+63 555 123 4567"
            />

            <InfoRow
              icon={<MapPin />}
              label="Address"
              value="Purok 3, Guinobatan, Trinidad, Bohol"
            />

            <InfoRow
              icon={<Mail />}
              label="Email"
              value="hopegardenvoh@gmail.com"
            />

            <InfoRow
              icon={<Globe />}
              label="Website / FB"
              value="www.villageofhope.site"
            />

            <div className="mt-8 text-center bg-emerald-900/30 py-4 rounded-2xl border border-emerald-700">
              <p className="text-emerald-300 font-bold text-lg">
                Protecting children, providing hope.
              </p>
            </div>

            {/* TEAM */}
            <div className="bg-slate-800 border border-slate-700 rounded-3xl p-6 shadow-md">
              <h3 className="text-lg font-bold text-center mb-4 text-slate-200">
                Capstone Development Team
              </h3>

              <TeamMember role="Project Manager" name="Erl Princes Llorente" />
              <TeamMember role="Developer" name="Jevie Boniel" />
              <TeamMember role="System Analyst" name="Ernest Mae Gaco" />
              <TeamMember role="UI/UX Designer" name="Geraldine Butil" />
              <TeamMember role="Tester" name="Grace Boyonas" />
            </div>

          </div>
        </div>
      </div>

      {/* LOGIN MODAL */}
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)}>
          
          <div className="bg-slate-800 shadow-2xl p-8 rounded-3xl border border-slate-700">

            <div className="flex flex-col items-center mb-6">
              <div className="h-20 w-20 rounded-full bg-slate-700 shadow-lg overflow-hidden border border-slate-600 flex items-center justify-center">
                <img src="/assets/hope.png" alt="Logo" className="h-full w-full object-cover" />
              </div>

              <h2 className="mt-4 text-2xl font-bold text-slate-100">
                Village of Hope
              </h2>

              <p className="mt-1 text-sm text-slate-400 text-center">
                Child Tracking & Development Monitoring
              </p>
            </div>

            {/* SAME FORM (UNCHANGED) */}
            <form className="space-y-5" onSubmit={handleSubmit}>

              {error && (
                <div className="rounded-2xl bg-red-900/30 border border-red-700 px-4 py-3">
                  <p className="text-red-300 text-sm font-medium">{error}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-slate-300">Email</label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-600 bg-slate-900 text-slate-100"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-300">Password</label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 pl-10 pr-10 rounded-xl border border-slate-600 bg-slate-900 text-slate-100"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>

              <Button type="submit" loading={loading} className="w-full">
                Sign in
              </Button>

            </form>

          </div>

        </LoginModal>
      )}
    </div>
  );
};

/* MODAL WRAPPER */
const LoginModal = ({ onClose, children }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div className="relative w-full max-w-md">
      <button
        onClick={onClose}
        className="absolute -top-10 right-0 text-slate-300 hover:text-white"
      >
        ✕
      </button>
      {children}
    </div>
  </div>
);

/* COMPONENTS */

const Feature = ({ icon, title }) => (
  <div className="flex items-center gap-3 bg-slate-800 rounded-2xl p-4 border border-slate-700">
    <div className="h-10 w-10 flex items-center justify-center bg-emerald-900 rounded-xl text-emerald-300">
      {icon}
    </div>
    <span className="font-semibold text-slate-200">{title}</span>
  </div>
);

const HotlineCard = ({ icon, title, number }) => (
  <div className="flex items-center gap-4 bg-slate-800 p-5 rounded-2xl border border-slate-700">
    <div className="h-12 w-12 flex items-center justify-center bg-cyan-900 rounded-xl text-cyan-300">
      {icon}
    </div>
    <div>
      <div className="font-bold text-slate-200">{title}</div>
      <div className="text-emerald-400 font-extrabold text-lg">{number}</div>
    </div>
  </div>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-2xl border border-slate-700 bg-slate-800 p-3">
    <div className="h-8 w-8 rounded-xl bg-slate-700 flex items-center justify-center text-slate-200">
      {icon}
    </div>
    <div className="text-sm">
      <div className="text-slate-400 font-semibold">{label}</div>
      <div className="text-slate-200 font-medium">{value}</div>
    </div>
  </div>
);

const TeamMember = ({ role, name }) => (
  <div className="flex justify-between border-b border-slate-700 py-2 text-sm text-slate-200">
    <span className="font-semibold text-emerald-400">{role}</span>
    <span>{name}</span>
  </div>
);

export default Login;